// ---------------------------------------------------------------------------
// La hiérarchie du menu de changement d'épisode, sans le DOM.
//
// Tout ce qui décide OÙ l'on est et CE QU'ON PROPOSE vit ici ; `main.js` ne
// garde que le dessin. La raison est bête et coûteuse : la partie qui se
// trompe, dans un menu à quatre niveaux, c'est le niveau — pas la peinture.
// Sortie du navigateur, elle s'éprouve au banc avec le vrai découpage en arcs
// (`grouperParArc`), donc avec les vraies parties de la Guerre Sanglante.
//
// Aucune de ces fonctions ne touche au réseau ni au document : elles lisent
// `serie.arcs`, qui est déjà en mémoire.
// ---------------------------------------------------------------------------

/**
 * L'index de l'arc qui contient cet épisode, ou `null`.
 *
 * C'est ce qui fait que le menu s'ouvre DÉJÀ POSITIONNÉ : sur l'épisode 412,
 * il ouvre la partie 4 de la Guerre Sanglante, parce que c'est là qu'on est.
 */
export function arcDeLEpisode(arcs, saison, episode) {
  const table = arcs ?? [];
  for (let i = 0; i < table.length; i += 1) {
    if ((table[i].episodes ?? []).some((v) => v.season === saison && v.episode === episode)) return i;
  }
  return null;
}

/**
 * Les entrées du niveau « arcs ».
 *
 * Un arc découpé en parties n'en occupe QU'UNE : ses parties sont proposées au
 * niveau du dessous. C'est le même regroupement que le menu de l'écran des
 * épisodes — s'en écarter donnerait quatre lignes « La guerre sanglante de
 * mille ans » à la suite, qu'on ne saurait pas distinguer.
 */
export function entreesArcs(arcs) {
  const entrees = [];
  (arcs ?? []).forEach((a, i) => {
    const precedent = entrees[entrees.length - 1];
    if (a.partie && precedent && precedent.nom === a.nom) {
      precedent.parties.push({ partie: a.partie, index: i });
      return;
    }
    entrees.push({
      nom: a.nom,
      filler: a.filler,
      index: i,
      parties: a.partie ? [{ partie: a.partie, index: i }] : [],
    });
  });
  return entrees;
}

/**
 * Le chemin sur lequel le menu s'ouvre.
 *
 * On rend la PILE complète, pas seulement le niveau visible : c'est elle qui
 * donne le bouton de retour. Ouvrir directement sur les épisodes d'une partie
 * sans pile derrière laisserait la personne enfermée dans cette partie.
 *
 * L'ordre des niveaux suit exactement le chemin de remontée demandé :
 * épisodes → parties → arcs → collections.
 */
export function cheminDeDepart(arcs, ou) {
  const i = ou ? arcDeLEpisode(arcs, ou.saison, ou.episode) : null;
  if (i === null) return (arcs ?? []).length ? [{ genre: 'arcs' }] : [{ genre: 'collections' }];

  const chemin = [{ genre: 'collections' }, { genre: 'arcs' }];
  // La partie n'est un niveau que si l'arc en a plusieurs : sur un arc d'un
  // seul tenant, un étage « Part 1 » à traverser ne dirait rien à personne.
  const entree = entreesArcs(arcs).find(
    (e) => e.index === i || e.parties.some((p) => p.index === i),
  );
  if (entree && entree.parties.length > 1) chemin.push({ genre: 'parties', entree });
  chemin.push({ genre: 'episodes', arc: i });
  return chemin;
}

/**
 * L'épisode d'avant et celui d'après, DANS LE CATALOGUE.
 *
 * ⚠️ Ceci remplace une lecture des grilles de la page, qui n'y trouvait
 * souvent rien. Depuis Discord, la lecture part dans un onglet ouvert sur un
 * billet (`/s/<billet>`) : cette adresse ne porte aucun fragment, la position
 * mémorisée est donc vide, l'écran de la série retombe sur l'arc 0 et dessine
 * les épisodes 1 à 20 pendant que le lecteur ouvre le 412. Aucune grille ne
 * contenait l'épisode joué, les deux voisins revenaient nuls, et les boutons
 * « précédent » et « suivant » restaient éteints pour de bon.
 *
 * La liste à plat des arcs ne dépend d'aucun rendu et franchit les frontières
 * d'arc : revenir du premier épisode d'une partie au dernier de la précédente
 * marche, alors que la grille ne montrait jamais qu'un arc à la fois.
 *
 * @param jouable dit si l'on a le droit d'y aller — un épisode pas encore
 *        diffusé n'est pas un voisin, c'est une impasse.
 */
export function voisinsCatalogue(arcs, saison, episode, jouable = () => true) {
  const liste = (arcs ?? []).flatMap((a) => a.episodes ?? []);
  const i = liste.findIndex((v) => v.season === saison && v.episode === episode);
  if (i < 0) return { precedent: null, suivant: null };
  const prendre = (n) => (liste[n] && jouable(liste[n]) ? liste[n] : null);
  return { precedent: prendre(i - 1), suivant: prendre(i + 1) };
}

/**
 * Les parties de l'arc où l'on se trouve, et celle qu'on regarde.
 *
 * L'en-tête du menu répétait le nom de l'arc des deux côtés : « ← La guerre
 * sanglante de mille ans » à gauche, « La guerre sanglante de mille ans —
 * Part 4 » à droite. La seule chose que la droite apprenait, c'était « Part 4 »
 * — noyée dans huit mots déjà lus. Elle ne dit donc plus QUE la partie, et
 * cette fonction lui donne de quoi proposer les autres sans remonter d'un
 * niveau : un arc de quatre parties se parcourt par sa droite.
 *
 * Un arc d'un seul tenant rend une liste vide : il n'y a rien à choisir, et
 * une commande « Part 1 » sans « Part 2 » derrière serait une fausse promesse.
 */
export function partiesDeLArc(arcs, index) {
  const entree = entreesArcs(arcs).find(
    (e) => e.index === index || e.parties.some((p) => p.index === index),
  );
  const parties = entree?.parties ?? [];
  if (parties.length < 2) return { nom: entree?.nom ?? null, parties: [], courante: null };
  return {
    nom: entree.nom,
    parties,
    courante: parties.find((p) => p.index === index) ?? null,
  };
}

// ---------------------------------------------------------------------------
// La recherche d'épisode.
//
// Elle porte sur TOUT le catalogue, jamais sur le seul arc ouvert : taper un
// numéro n'a d'intérêt que si l'on n'a pas déjà à savoir où il tombe. Chercher
// « 412 » depuis la partie 1 doit ramener la fin de la partie 4 — sinon il
// fallait connaître le découpage pour s'en servir, et le découpage est
// justement ce qu'on cherche à éviter de connaître.
// ---------------------------------------------------------------------------

// Les marques diacritiques laissées par la décomposition NFD. On les retire
// des DEUX côtés — requête et titres — parce que personne ne compose « Sôsuke »
// correctement en tapant vite, et qu'un accent manquant ne doit pas coûter un
// résultat.
const DIACRITIQUES = /[\u0300-\u036f]/g;

/**
 * La forme sous laquelle on compare. Casse, accents et ponctuation tombent.
 *
 * La ponctuation devient une espace plutôt que de disparaître : « L'agent des
 * Shinigami » donne « l agent des shinigami », et la même règle appliquée à ce
 * qu'on tape fait correspondre les deux orthographes de l'apostrophe (droite et
 * courbe), qui cohabitent dans les titres du catalogue.
 */
export function normaliserRecherche(valeur) {
  return String(valeur ?? '')
    .normalize('NFD')
    .replace(DIACRITIQUES, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

// Les champs où un titre peut se trouver.
//
// Le catalogue n'en sert aujourd'hui QU'UN, `title`, dont la langue dépend de
// la source : elle est française sur la Guerre Sanglante (« LA FIN »), anglaise
// ailleurs. On indexe donc tous les noms de champ plausibles d'un coup — le
// jour où une seconde langue arrive à côté de la première, la recherche la
// prend sans qu'on ait à revenir ici, et rien ne coûte pour les champs absents.
const CHAMPS_TITRE = [
  'title', 'name', 'titre', 'nom',
  'titleFr', 'titreFr', 'nameFr', 'title_fr', 'nom_fr',
  'titleEn', 'titreEn', 'nameEn', 'title_en', 'nom_en',
];
const CHAMPS_LISTE = ['titres', 'noms', 'aliases'];

/** Tous les libellés d'un épisode, déjà normalisés et dédoublonnés. */
function libellesDe(v) {
  const bruts = [];
  for (const c of CHAMPS_TITRE) if (typeof v[c] === 'string') bruts.push(v[c]);
  for (const c of CHAMPS_LISTE) if (Array.isArray(v[c])) bruts.push(...v[c]);
  const vus = new Set();
  for (const b of bruts) {
    const n = normaliserRecherche(b);
    if (n) vus.add(n);
  }
  // Le code de diffusion s'écrit tel qu'on le lit dans les noms de fichiers et
  // dans les annonces du salon : c'est la troisième façon dont on désigne un
  // épisode, après son titre et son numéro absolu.
  vus.add(`s${v.season}e${v.episode}`);
  return [...vus];
}

/**
 * L'index, construit une fois pour toutes à l'ouverture du menu.
 *
 * On normalise à la CONSTRUCTION, pas à la frappe : un catalogue de quatre
 * cents épisodes traversé à chaque touche referait quatre cents décompositions
 * NFD pour rien. Ici la frappe ne fait plus que comparer des chaînes déjà
 * prêtes.
 *
 * Chaque entrée garde l'index de son arc : un résultat est cliquable, et il
 * doit pouvoir ramener le menu sur l'arc où il vit.
 */
export function indexerCatalogue(arcs) {
  const index = [];
  (arcs ?? []).forEach((a, i) => {
    const contexte = a.partie ? `${a.nom} — Part ${a.partie}` : a.nom;
    for (const v of a.episodes ?? []) {
      index.push({
        arc: i,
        nomArc: a.nom,
        partie: a.partie ?? null,
        contexte,
        video: v,
        saison: v.season,
        episode: v.episode,
        absolu: v.absolu ?? null,
        libelles: libellesDe(v),
      });
    }
  });
  return index;
}

/**
 * Ce que la recherche rend, du plus sûr au plus vague.
 *
 * Les notes ne sont pas décoratives : taper « 46 » désigne d'abord le 46e
 * épisode de la série, et seulement ensuite le S17E46 — sans cet ordre, la
 * réponse dépendrait de l'ordre du catalogue, donc du hasard. Un titre trouvé
 * en entier passe devant un titre trouvé par son début, qui passe devant un
 * titre où le mot est perdu au milieu.
 *
 * Le nombre est cherché sur le numéro ABSOLU et sur le numéro dans la saison,
 * jamais sur la saison elle-même : « 1 » rendrait alors les trois cent
 * soixante-six épisodes de la saison 1, ce qui n'est pas une réponse.
 */
export function chercherDansCatalogue(index, requete, max = 60) {
  const q = normaliserRecherche(requete);
  if (!q) return [];
  const nombre = /^[0-9]+$/.test(q) ? Number(q) : null;

  const trouves = [];
  for (const e of index ?? []) {
    let note = 0;
    if (nombre !== null) {
      if (e.absolu === nombre) note = 100;
      else if (e.episode === nombre) note = Math.max(note, 60);
    }
    for (const l of e.libelles) {
      if (l === q) note = Math.max(note, 90);
      else if (l.startsWith(q)) note = Math.max(note, 70);
      else if (l.includes(q)) note = Math.max(note, 40);
    }
    if (note) trouves.push({ ...e, note });
  }

  // À note égale, l'ordre du catalogue : c'est celui dans lequel on les a vus,
  // et il est stable d'une frappe à l'autre. Un tri par pertinence seule ferait
  // sauter les lignes sous le doigt pendant qu'on complète le mot.
  trouves.sort((a, b) => b.note - a.note
    || (a.absolu ?? Infinity) - (b.absolu ?? Infinity)
    || a.saison - b.saison || a.episode - b.episode);
  return trouves.slice(0, max);
}
